import { Users, Target, TrendingUp, BarChart3, Building, Lightbulb, Zap } from 'lucide-react';
import type { Product } from './types';

export const consultingProducts: Product[] = [
  {
    id: 'business-strategy-consulting',
    title: 'Business Strategy Consulting Platform',
    subtitle: 'Strategic planning and execution',
    description: 'Comprehensive business strategy consulting with UK market analysis, competitive intelligence, and strategic planning tools for British enterprises.',
    icon: Target,
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Business Applications',
    status: 'Available',
    businessTypes: ['#consulting', '#strategy', '#business-planning', '#enterprise', '#advisory'],
    features: [
      'Strategic Planning & Roadmapping for UK markets',
      'UK Market Analysis & Research Tools',
      'Competitive Intelligence Platform',
      'SWOT Analysis & Framework Tools',
      'Business Model Canvas & Planning',
      'Performance Metrics & KPI Tracking',
      'Stakeholder Management & Communication',
      'Implementation Planning & Monitoring'
    ],
    pricing: { setup: 35000, monthly: 1610 },
    color: 'from-blue-500 to-indigo-600'
  },
  {
    id: 'management-consulting-suite',
    title: 'Management Consulting Suite',
    subtitle: 'Organisational excellence platform',
    description: 'Complete management consulting toolkit with organisational assessment, process optimisation, and change management capabilities for UK businesses.',
    icon: Users,
    image: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Business Applications',
    status: 'Available',
    businessTypes: ['#consulting', '#management', '#organisational-development', '#change-management', '#enterprise'],
    features: [
      'Organisational Assessment & Analysis',
      'Process Mapping & Optimisation',
      'Change Management & Implementation',
      'Leadership Development Programs',
      'Culture Transformation Tools',
      'Performance Improvement Planning',
      'Team Effectiveness Analysis',
      'Executive Coaching & Development'
    ],
    pricing: { setup: 35000, monthly: 1610 },
    color: 'from-green-500 to-teal-600'
  },
  {
    id: 'financial-advisory-platform',
    title: 'Financial Advisory Platform',
    subtitle: 'Comprehensive financial consulting',
    description: 'Advanced financial advisory platform with investment analysis, risk assessment, and portfolio management tools.',
    icon: TrendingUp,
    image: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Business Applications',
    status: 'Available',
    businessTypes: ['#financial-advisory', '#investment', '#wealth-management', '#consulting', '#portfolio-management'],
    features: [
      'Investment Analysis & Research',
      'Portfolio Management & Optimisation',
      'Risk Assessment & Management',
      'Financial Planning & Forecasting',
      'Market Analysis & Insights',
      'Client Relationship Management',
      'Regulatory Compliance & Reporting',
      'Performance Tracking & Analytics'
    ],
    pricing: { setup: 38000, monthly: 1748 },
    color: 'from-purple-500 to-pink-600'
  },
  {
    id: 'digital-transformation-consulting',
    title: 'Digital Transformation Consulting',
    subtitle: 'Technology modernisation platform',
    description: 'Comprehensive digital transformation consulting with technology assessment, digital strategy, and implementation planning.',
    icon: Zap,
    image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Business Applications',
    status: 'Available',
    businessTypes: ['#digital-transformation', '#technology-consulting', '#modernisation', '#enterprise', '#digital-strategy'],
    features: [
      'Digital Maturity Assessment',
      'Technology Stack Analysis',
      'Digital Strategy Development',
      'Implementation Roadmapping',
      'Change Management Support',
      'ROI Analysis & Business Case',
      'Vendor Selection & Management',
      'Training & Adoption Planning'
    ],
    pricing: { setup: 40000, monthly: 1840 },
    color: 'from-orange-500 to-red-600'
  },
  {
    id: 'operational-excellence-platform',
    title: 'Operational Excellence Platform',
    subtitle: 'Process optimisation consulting',
    description: 'Advanced operational excellence consulting with lean methodologies, process improvement, and efficiency optimisation.',
    icon: BarChart3,
    image: 'https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Business Applications',
    status: 'Available',
    businessTypes: ['#operational-excellence', '#process-improvement', '#lean-consulting', '#efficiency', '#manufacturing'],
    features: [
      'Process Mapping & Analysis',
      'Lean Six Sigma Implementation',
      'Waste Reduction & Optimisation',
      'Performance Metrics & KPIs',
      'Continuous Improvement Programs',
      'Quality Management Systems',
      'Cost Reduction Strategies',
      'Operational Efficiency Planning'
    ],
    pricing: { setup: 36000, monthly: 1656 },
    color: 'from-cyan-500 to-blue-600'
  },
  {
    id: 'innovation-consulting-platform',
    title: 'Innovation Consulting Platform',
    subtitle: 'Innovation strategy and execution',
    description: 'Comprehensive innovation consulting with ideation tools, innovation management, and product development support.',
    icon: Lightbulb,
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Business Applications',
    status: 'Available',
    businessTypes: ['#innovation-consulting', '#product-development', '#r-and-d', '#creative-strategy', '#startup'],
    features: [
      'Innovation Strategy Development',
      'Ideation & Brainstorming Tools',
      'Product Development Framework',
      'Innovation Pipeline Management',
      'Market Opportunity Analysis',
      'Technology Scouting & Assessment',
      'Innovation Metrics & ROI',
      'Intellectual Property Strategy'
    ],
    pricing: { setup: 38000, monthly: 1748 },
    color: 'from-yellow-500 to-orange-600'
  },
  {
    id: 'sustainability-consulting-suite',
    title: 'Sustainability Consulting Suite',
    subtitle: 'ESG and sustainability platform',
    description: 'Complete sustainability consulting with ESG assessment, carbon footprint tracking, and sustainability strategy development.',
    icon: Building,
    image: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Business Applications',
    status: 'Available',
    businessTypes: ['#sustainability-consulting', '#esg', '#carbon-tracking', '#green-business', '#corporate-responsibility'],
    features: [
      'ESG Assessment & Reporting',
      'Carbon Footprint Tracking',
      'Sustainability Strategy Development',
      'Green Supply Chain Analysis',
      'Environmental Impact Assessment',
      'Stakeholder Engagement Tools',
      'Regulatory Compliance Support',
      'Sustainability Performance Metrics'
    ],
    pricing: { setup: 42000, monthly: 1932 },
    color: 'from-emerald-500 to-green-600'
  }
];
